/**
 * OPVisual session - what OPButtons has SENT to OPVisual this session.
 *
 * ## This is not OPVisual's state, and nothing here may claim it is
 *
 * What OPVisual REPORTS lives in `live.ts` (DEC-272). This file is what we
 * SENT, and the grid falls back to it whenever OPVisual is not reporting.
 * So every value here is either:
 *
 * - a CONTROL POSITION (opacity, speed, blend, the look sliders): where our
 *   control was last put. Starts at OPVisual's documented default so the
 *   first gesture moves from somewhere sensible, and `touched` says whether
 *   we have ever actually sent it - an untouched control must not display a
 *   number as though it had been read.
 * - a SENT MARK (the cell or column last fired): rendered as "sent", never as
 *   "on air". PLAN.md's Rig State honesty rule forbids intent masquerading as
 *   confirmed truth, and a bar-quantised fire is up to a bar away from landing
 *   anyway.
 *
 * Plain signal over an immutable record, never a Map (a Solid read of a Map
 * tracks nothing - same reasoning as `obs/scenes.ts`). Global, not keyed by
 * bridge: there is one OPVisual.
 */

import { createSignal, type Accessor } from 'solid-js';
import { OPVISUAL_LAYERS, type OpvBlend, type OpvLookParam } from './wire.ts';

export interface OpvLayerState {
  opacity: number;
  enabled: boolean;
  blend: OpvBlend;
  speed: number;
  /** Which of the above we have actually sent at least once. */
  touched: { opacity: boolean; enabled: boolean; blend: boolean; speed: boolean };
}

export interface OpvSession {
  /** Index 0 = layer 1 (L1, bottom). */
  layers: OpvLayerState[];
  /** Last cell sent per layer (index 0 = L1), or null. */
  sentCell: Array<number | null>;
  /** Last column sent, or null. */
  sentColumn: number | null;
  master: number;
  masterTouched: boolean;
  look: Record<OpvLookParam, number | null>;
  /** Monotonic, bumped on every send - lets a cell flash on each fire even
   *  when the same cell is fired twice in a row. */
  seq: number;
}

function freshLayer(): OpvLayerState {
  return {
    opacity: 1,
    enabled: true,
    blend: 'normal',
    speed: 1,
    touched: { opacity: false, enabled: false, blend: false, speed: false },
  };
}

function fresh(): OpvSession {
  return {
    layers: Array.from({ length: OPVISUAL_LAYERS }, freshLayer),
    sentCell: Array.from({ length: OPVISUAL_LAYERS }, () => null),
    sentColumn: null,
    master: 1,
    masterTouched: false,
    look: { brightness: null, hue: null, sat: null, bloom: null },
    seq: 0,
  };
}

const [sessionSig, setSessionSig] = createSignal<OpvSession>(fresh());

export const opvSession: Accessor<OpvSession> = sessionSig;

function bump(next: OpvSession): OpvSession {
  return { ...next, seq: next.seq + 1 };
}

/** OPVisual gives every newly placed clip speed 1.0 (`media/deck.rs`), and
 *  a remote speed only ever applied to the clip that was on the lane. So a
 *  fire onto a lane puts OUR speed readout back to its default-and-untouched
 *  state rather than keep showing a speed the new clip does not have. */
function resetSpeed(l: OpvLayerState): OpvLayerState {
  return { ...l, speed: 1, touched: { ...l.touched, speed: false } };
}

export function markCellSent(col: number, layer: number): void {
  setSessionSig((s) => {
    const sentCell = [...s.sentCell];
    sentCell[layer - 1] = col;
    const layers = s.layers.map((l, i) => (i === layer - 1 ? resetSpeed(l) : l));
    return bump({ ...s, sentCell, sentColumn: null, layers });
  });
}

/** A column is one whole look: every layer is asked for that column. */
export function markColumnSent(col: number): void {
  setSessionSig((s) => bump({
    ...s,
    sentCell: s.sentCell.map(() => col),
    sentColumn: col,
    layers: s.layers.map(resetSpeed),
  }));
}

export function markLayerCleared(layer: number): void {
  setSessionSig((s) => {
    const sentCell = [...s.sentCell];
    sentCell[layer - 1] = null;
    return bump({ ...s, sentCell, sentColumn: null });
  });
}

/** STOP ALL takes every lane off air, so only the sent marks move. */
export function markAllCleared(): void {
  setSessionSig((s) => bump({
    ...s,
    sentCell: s.sentCell.map(() => null),
    sentColumn: null,
  }));
}

/** PANIC takes every lane off air AND puts OPVisual's look back to neutral
 *  at FULL master (OPVisual DEC-122, 2026-09-28; measured by its bot:
 *  `panic_releases_blackout_and_restores_full_master`). So the master and
 *  look controls go back to their fresh-session default-and-untouched state:
 *  a slider left at 40% would otherwise claim a master OPVisual no longer
 *  has. Layer controls are not part of that reset and do not move. */
export function markPanicked(): void {
  setSessionSig((s) => bump({
    ...s,
    sentCell: s.sentCell.map(() => null),
    sentColumn: null,
    master: 1,
    masterTouched: false,
    look: { brightness: null, hue: null, sat: null, bloom: null },
  }));
}

export function setLayerLocal<K extends 'opacity' | 'enabled' | 'blend' | 'speed'>(
  layer: number, key: K, value: OpvLayerState[K],
): void {
  setSessionSig((s) => {
    const layers = s.layers.map((l, i) => (i === layer - 1
      ? { ...l, [key]: value, touched: { ...l.touched, [key]: true } }
      : l));
    return bump({ ...s, layers });
  });
}

export function setMasterLocal(v: number): void {
  setSessionSig((s) => bump({ ...s, master: v, masterTouched: true }));
}

export function setLookLocal(param: OpvLookParam, v: number): void {
  setSessionSig((s) => bump({ ...s, look: { ...s.look, [param]: v } }));
}

// ============================================================
// Link - what the LAST OPBridge-protocol send to each machine found
// ============================================================

/**
 * Bridge row ids whose most recent `POST /opvisual/trigger` answered
 * `opvisual_connected: false` (DEC-215). Set and cleared ONLY by a send's own
 * answer - never polled - so it says what the last press found, not what is
 * true right now. That is why it never disables a control: the next press is
 * both the retry and the re-check, and it clears this the moment OPVisual
 * answers again. OSC sends never touch it; that channel cannot tell.
 *
 * Keyed by bridge row id (the one OPVisual machine), plain array in a signal
 * for the same Map-tracks-nothing reason as the session above.
 */
const [linkDownSig, setLinkDownSig] = createSignal<readonly string[]>([]);

export function setOpvLinkDown(bridgeRowId: string, down: boolean): void {
  setLinkDownSig((prev) => {
    const has = prev.includes(bridgeRowId);
    if (down === has) return prev;
    return down ? [...prev, bridgeRowId] : prev.filter((id) => id !== bridgeRowId);
  });
}

export function opvLinkDownFor(bridgeRowId: string | undefined): boolean {
  return !!bridgeRowId && linkDownSig().includes(bridgeRowId);
}

/** Tests and the bot. */
export function resetOpvSession(): void {
  setSessionSig(fresh());
  setLinkDownSig([]);
}
